import { useEffect, useMemo, useState } from "react";
import {
    Card, Select, DatePicker, Typography, Row, Col, Tabs, Table, Button, Space, Tag, Empty, Spin,
} from "antd";
import { CalendarOutlined, FileExcelOutlined, FilePdfOutlined, BankOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import axiosInstance from "../../../variables/api/axiosInstance";
import SideBar from "../../../components/SideBar";
import NavBar from "../../../components/NavBar";
import Footer from "../../../components/Footer";
import { displayNumbers } from "../../../variables/numbersFunctions";
import { frenchDateOnly } from "../../../variables/functions";
import { generatePrimesAReporterPDF } from "../../production/etatsProductionFunctions";

const { Title, Text } = Typography;
const PRIMARY = "#242166";

/**
 * TABLEAU DE RÉPARTITION DES PRIMES À REPORTER (PREC). Par exercice + date de souscription (du 1er
 * janvier au jour choisi). Récap identique au document officiel + détail des contrats justificatifs.
 * Données : GET /api/stats/primes-a-reporter (+ /detail, + /exercices).
 */
const PrimeAReporterEtat = () => {
    const [exercices, setExercices] = useState([]);
    const [exercice, setExercice] = useState(null);
    const [dateSous, setDateSous] = useState(null); // dayjs | null (null = 31 déc de l'exercice)
    const [recap, setRecap] = useState(null);
    const [detail, setDetail] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        axiosInstance.get("/api/stats/primes-a-reporter/exercices")
            .then((r) => {
                const list = Array.isArray(r.data) ? r.data : [];
                setExercices(list);
                if (list.length) setExercice(list[0]);
            })
            .catch(() => toast.error("Erreur lors du chargement des exercices."));
    }, []);

    // Rechargement dès qu'un exercice (et/ou la date) change. La date reste dans l'exercice choisi.
    useEffect(() => {
        if (!exercice) return;
        const params = { exercice };
        if (dateSous) params.dateSouscription = dateSous.format("YYYY-MM-DD");
        setLoading(true);
        Promise.all([
            axiosInstance.get("/api/stats/primes-a-reporter", { params }),
            axiosInstance.get("/api/stats/primes-a-reporter/detail", { params }),
        ])
            .then(([resRecap, resDetail]) => {
                setRecap(resRecap.data || null);
                setDetail(Array.isArray(resDetail.data) ? resDetail.data : []);
            })
            .catch(() => toast.error("Erreur lors du chargement des primes à reporter."))
            .finally(() => setLoading(false));
    }, [exercice, dateSous]);

    const branches = recap?.branches || [];

    // Ligne TOTAUX : quote-part cumulée (≈ 100 %) par branche + montants = totaux par branche.
    const repartitionRows = useMemo(() => {
        if (!recap) return [];
        const rows = (recap.repartition || []).map((c) => ({ ...c, key: c.compagnieCode }));
        const qpTot = {};
        branches.forEach((br) => {
            qpTot[br] = rows.reduce((s, r) => s + (Number(r.quotePartParBranche?.[br]) || 0), 0);
        });
        rows.push({
            key: "__totaux__", isTotal: true, compagnieNom: "TOTAUX",
            quotePartParBranche: qpTot,
            montantParBranche: recap.totauxParBranche || {},
            montantNetteParBranche: recap.totauxNetteParBranche || {},
            montantAccessoiresParBranche: recap.totauxAccessoiresParBranche || {},
            totalCompagnie: recap.totalGeneral || 0,
        });
        return rows;
    }, [recap, branches]);

    const repartitionColumns = useMemo(() => {
        const cols = [{
            title: "SOCIÉTÉS D'ASSURANCES", dataIndex: "compagnieNom", key: "cie", fixed: "left", width: 240,
            render: (v, r) => (r.isTotal ? <strong>{v}</strong> : v),
        }];
        branches.forEach((br) => {
            cols.push({
                title: br,
                children: [
                    {
                        title: "QUOTE PART", key: `${br}-qp`, align: "center", width: 90,
                        render: (_, r) => {
                            const v = r.quotePartParBranche?.[br];
                            const txt = (v != null && v !== undefined) ? `${(+v).toLocaleString("fr-FR")}%` : "—";
                            return r.isTotal ? <strong>{txt}</strong> : txt;
                        },
                    },
                    {
                        // Part sur la prime NETTE (mutualisée par la clé).
                        title: "NETTE", key: `${br}-net`, align: "right", width: 120,
                        render: (_, r) => {
                            const txt = displayNumbers(r.montantNetteParBranche?.[br] || 0);
                            return r.isTotal ? <strong>{txt}</strong> : txt;
                        },
                    },
                    {
                        // Part sur les ACCESSOIRES (propre à la compagnie, si branche configurée).
                        title: "ACCESSOIRES", key: `${br}-acc`, align: "right", width: 120,
                        render: (_, r) => {
                            const txt = displayNumbers(r.montantAccessoiresParBranche?.[br] || 0);
                            return r.isTotal ? <strong>{txt}</strong> : txt;
                        },
                    },
                    {
                        title: "TOTAL", key: `${br}-mt`, align: "right", width: 130,
                        render: (_, r) => {
                            const txt = displayNumbers(r.montantParBranche?.[br] || 0);
                            return <strong>{txt}</strong>;
                        },
                    },
                ],
            });
        });
        cols.push({
            title: "TOTAL PAR COMPAGNIE", key: "totalCie", align: "right", width: 150, fixed: "right",
            render: (_, r) => <strong>{displayNumbers(r.totalCompagnie || 0)}</strong>,
        });
        return cols;
    }, [branches]);

    const detailColumns = [
        { title: "N° Police", dataIndex: "numeroPolice", key: "police", render: (v) => <Text strong style={{ color: PRIMARY }}>{v}</Text> },
        { title: "Assuré", dataIndex: "assure", key: "assure", render: (v) => v || "—" },
        { title: "Compagnie", dataIndex: "compagnie", key: "compagnie", render: (v) => v || "—" },
        { title: "Branche", dataIndex: "branche", key: "branche", render: (v) => <Tag color="blue">{v}</Tag> },
        { title: "Effet", dataIndex: "dateEffet", key: "effet", render: (v) => (v ? frenchDateOnly(v) : "") },
        { title: "Échéance", dataIndex: "dateEcheance", key: "echeance", render: (v) => (v ? frenchDateOnly(v) : "") },
        { title: "Prime nette", dataIndex: "primeNette", key: "primeNette", align: "right", render: (v) => displayNumbers(v || 0) },
        { title: "Accessoires", dataIndex: "accessoires", key: "accessoires", align: "right", render: (v) => displayNumbers(v || 0) },
        { title: "Assiette (nette + acc.)", dataIndex: "assiette", key: "assiette", align: "right", render: (v, r) => displayNumbers((v != null ? v : (Number(r.primeNette) || 0) + (Number(r.accessoires) || 0))) },
        { title: "Prime à reporter", dataIndex: "primeAReporter", key: "prec", align: "right", render: (v) => <strong>{displayNumbers(v || 0)}</strong> },
    ];

    const detailTotal = useMemo(() => detail.reduce((s, d) => s + (Number(d.primeAReporter) || 0), 0), [detail]);
    const detailTotalPrime = useMemo(() => detail.reduce((s, d) => s + (Number(d.primeNette) || 0), 0), [detail]);
    const detailTotalAccessoires = useMemo(() => detail.reduce((s, d) => s + (Number(d.accessoires) || 0), 0), [detail]);
    const detailTotalAssiette = useMemo(() => detail.reduce((s, d) => s + (Number(d.assiette != null ? d.assiette : (Number(d.primeNette) || 0) + (Number(d.accessoires) || 0))) , 0), [detail]);

    const periodeLabel = `Exercice ${exercice || "—"}${dateSous ? ` — vu au ${dateSous.format("DD/MM/YYYY")}` : ""}`;

    // ── Export Excel (récap + détail) ──────────────────────────────────────────
    const exportExcel = () => {
        if (!recap) return;
        const wb = XLSX.utils.book_new();
        const tr = (v) => Math.trunc(Number(v) || 0);
        const DATE_FMT = "dd/mm/yyyy"; // format d'affichage FORCÉ (lève l'ambiguïté jj/mm vs mm/jj)
        // Date en minuit LOCAL (year/month/day) → sérial Excel entier, jamais re-parsée en mm/jj.
        const toXlsDate = (v) => { if (!v) return null; const dj = dayjs(v); return new Date(dj.year(), dj.month(), dj.date()); };
        // Applique le format jj/mm/aaaa à des colonnes-dates (lettres) sur toutes les lignes de données.
        const forceDateFormat = (ws, cols, nbLignes) => {
            for (let i = 0; i < nbLignes; i++) {
                const r = i + 2; // en-tête ligne 1, données à partir de la ligne 2
                cols.forEach((c) => { if (ws[`${c}${r}`] && ws[`${c}${r}`].t === "d") ws[`${c}${r}`].z = DATE_FMT; });
            }
        };

        // Feuille 1 : Répartition
        const aoa = [
            ["TABLEAU DE RÉPARTITION DES PRIMES À REPORTER"],
            [`Exercice : ${exercice}`],
            [dateSous ? `Prime à reporter vu au : ${dateSous.format("DD/MM/YYYY")}` : "Prime à reporter vu au : 31/12 (exercice complet)"],
            [],
            ["PRIME TOTALE À REPORTER", "TOTAL", "dont NETTE", "dont ACCESSOIRES"],
            ...branches.map((br) => [
                br,
                tr(recap.totauxParBranche?.[br]),
                tr(recap.totauxNetteParBranche?.[br]),
                tr(recap.totauxAccessoiresParBranche?.[br]),
            ]),
            ["TOTAL", tr(recap.totalGeneral),
                tr(Object.values(recap.totauxNetteParBranche || {}).reduce((a, v) => a + (Number(v) || 0), 0)),
                tr(Object.values(recap.totauxAccessoiresParBranche || {}).reduce((a, v) => a + (Number(v) || 0), 0))],
            [],
        ];
        // En-têtes du tableau de répartition (2 lignes : branche / quote part+montant)
        const h1 = ["SOCIÉTÉS D'ASSURANCES"];
        const h2 = [""];
        branches.forEach((br) => { h1.push(br, "", "", ""); h2.push("QUOTE PART", "NETTE", "ACCESSOIRES", "TOTAL"); });
        h1.push("TOTAL PAR COMPAGNIE"); h2.push("");
        aoa.push(h1, h2);
        repartitionRows.forEach((r) => {
            const line = [r.compagnieNom];
            branches.forEach((br) => {
                const qp = r.quotePartParBranche?.[br];
                line.push(
                    qp != null ? `${qp}%` : "",
                    tr(r.montantNetteParBranche?.[br]),
                    tr(r.montantAccessoiresParBranche?.[br]),
                    tr(r.montantParBranche?.[br]),
                );
            });
            line.push(tr(r.totalCompagnie));
            aoa.push(line);
        });
        aoa.push([], ["Méthode de calcul : Prorata temporis — assiette = prime nette + accessoires."]);
        aoa.push(["NB : il revient à chaque société membre du Pool d'appliquer le taux approprié à la prime à reporter pour obtenir la PREC à provisionner."]);
        aoa.push(["Clés utilisées : clés de l'exercice " + exercice]);
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), "Répartition");

        // Feuille 2 : Détail des contrats — dates en vraies cellules-dates au format jj/mm/aaaa forcé.
        const detAoa = [["N° Police", "Assuré", "Compagnie", "Branche", "Effet", "Échéance", "Prime nette", "Accessoires", "Assiette (nette + acc.)", "Prime à reporter"]];
        detail.forEach((d) => {
            const assiette = d.assiette != null ? d.assiette : (Number(d.primeNette) || 0) + (Number(d.accessoires) || 0);
            detAoa.push([
                d.numeroPolice, d.assure || "", d.compagnie || "", d.branche,
                toXlsDate(d.dateEffet), toXlsDate(d.dateEcheance),
                tr(d.primeNette), tr(d.accessoires), tr(assiette), tr(d.primeAReporter),
            ]);
        });
        detAoa.push(["TOTAL", "", "", "", "", "", tr(detailTotalPrime), tr(detailTotalAccessoires), tr(detailTotalAssiette), tr(detailTotal)]);
        const ds = XLSX.utils.aoa_to_sheet(detAoa, { cellDates: true });
        forceDateFormat(ds, ["E", "F"], detail.length); // Effet, Échéance
        XLSX.utils.book_append_sheet(wb, ds, "Détail");

        // Feuille 3 : Vérification PREC — dates réelles + FORMULES Excel vivantes recalculant le PREC.
        // PREC = MAX(0 ; ROUND( prime_nette × (jours_restants+1) / (jours_couverture+1) ; 0)), franc entier,
        // clôture = 01/01/(exercice+1) — aligné sur la procédure stockée. « Écart » compare au système.
        const refCloture = new Date(Number(exercice) + 1, 0, 1); // 01/01/(exercice+1) = clôture de l'exercice
        const vHead = [
            "N° Police", "Compagnie", "Branche", "Date effet", "Date échéance",
            "Clôture (01/01/N+1)", "Assiette (nette + acc.)", "Jours couverture (éch.−effet)",
            "Jours après clôture (éch.−clôture)", "PREC recalculé (formule)", "PREC (système)", "Écart (système−recalcul)",
        ];
        const vAoa = [vHead];
        detail.forEach((d) => vAoa.push([
            d.numeroPolice, d.compagnie || "", d.branche,
            toXlsDate(d.dateEffet), toXlsDate(d.dateEcheance), refCloture,
            Number(d.assiette != null ? d.assiette : (Number(d.primeNette) || 0) + (Number(d.accessoires) || 0)) || 0,
            null, null, null,                    // H, I, J : formules injectées ci-dessous
            Number(d.primeAReporter) || 0,       // K : valeur système
            null,                                // L : écart (formule)
        ]));
        const vs = XLSX.utils.aoa_to_sheet(vAoa, { cellDates: true });
        for (let i = 0; i < detail.length; i++) {
            const r = i + 2; // en-tête = ligne 1, données à partir de la ligne 2
            vs[`H${r}`] = { t: "n", f: `E${r}-D${r}` };                                   // jours de couverture
            vs[`I${r}`] = { t: "n", f: `E${r}-F${r}` };                                   // jours après clôture
            // Formule alignée sur la procédure stockée update_prec_for_next_year_contracts :
            // +1 jour au numérateur (I) ET au dénominateur (H), arrondi au franc entier.
            vs[`J${r}`] = { t: "n", f: `IF(H${r}+1<=0,0,MAX(0,ROUND(G${r}*(I${r}+1)/(H${r}+1),0)))` }; // PREC recalculé
            vs[`L${r}`] = { t: "n", f: `K${r}-J${r}` };                                   // écart
        }
        forceDateFormat(vs, ["D", "E", "F"], detail.length); // Effet, Échéance, Clôture
        XLSX.utils.book_append_sheet(wb, vs, "Vérification PREC");

        XLSX.writeFile(wb, `Primes_a_reporter_${exercice}${dateSous ? "_au_" + dateSous.format("YYYY-MM-DD") : ""}.xlsx`);
    };

    const [exportingPdf, setExportingPdf] = useState(false);
    const exportPdf = async () => {
        if (!recap || !branches.length) return;
        setExportingPdf(true);
        try {
            const uri = await generatePrimesAReporterPDF({
                exercice,
                dateLabel: dateSous ? `Prime à reporter vu au ${dateSous.format("DD/MM/YYYY")}` : "",
                branches,
                totauxParBranche: recap.totauxParBranche || {},
                totauxNetteParBranche: recap.totauxNetteParBranche || {},
                totauxAccessoiresParBranche: recap.totauxAccessoiresParBranche || {},
                totalGeneral: recap.totalGeneral || 0,
                repartition: recap.repartition || [],
            });
            const a = document.createElement("a");
            a.href = uri;
            a.download = `Primes_a_reporter_${exercice}${dateSous ? "_au_" + dateSous.format("YYYY-MM-DD") : ""}.pdf`;
            a.click();
        } catch (e) {
            toast.error("Erreur lors de la génération du PDF.");
        } finally {
            setExportingPdf(false);
        }
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"STATS"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: "#111827" }}>
                                        <BankOutlined style={{ marginRight: 8, color: PRIMARY }} />Primes à reporter
                                    </Title>
                                    <Text type="secondary">Tableau de répartition des primes à reporter — {periodeLabel}</Text>
                                </div>
                                <Space wrap>
                                    <Button icon={<FileExcelOutlined />} onClick={exportExcel} disabled={!recap || !branches.length}
                                        style={!recap || !branches.length ? undefined : { borderColor: "#059669", color: "#059669" }}>
                                        Exporter Excel
                                    </Button>
                                    <Button icon={<FilePdfOutlined />} onClick={exportPdf} loading={exportingPdf} disabled={!recap || !branches.length}
                                        style={!recap || !branches.length ? undefined : { borderColor: "#dc2626", color: "#dc2626" }}>
                                        Exporter PDF
                                    </Button>
                                </Space>
                            </div>

                            {/* Filtres */}
                            <Card bordered={false} style={{ borderRadius: 12, marginBottom: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }} bodyStyle={{ padding: 12 }}>
                                <Row gutter={[12, 12]} align="middle">
                                    <Col xs={24} md={8}>
                                        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Exercice</div>
                                        <Select style={{ width: "100%" }} placeholder="Exercice" value={exercice || undefined}
                                            onChange={(v) => { setExercice(v); setDateSous(null); }}
                                            options={exercices.map((y) => ({ value: y, label: `${y}` }))} />
                                    </Col>
                                    <Col xs={24} md={10}>
                                        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Prime à reporter vu au (date de souscription)</div>
                                        <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" value={dateSous}
                                            onChange={(d) => setDateSous(d)} allowClear
                                            prefix={<CalendarOutlined />}
                                            placeholder="31/12 (exercice complet) par défaut"
                                            disabledDate={(d) => !exercice || d.year() !== exercice} />
                                    </Col>
                                    <Col xs={24} md={6}>
                                        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>&nbsp;</div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Données du 1<sup>er</sup> janvier {exercice || ""} à la date choisie · clés de l'exercice.
                                        </Text>
                                    </Col>
                                </Row>
                            </Card>

                            <Spin spinning={loading}>
                                <Tabs
                                    items={[
                                        {
                                            key: "recap", label: "Récapitulatif",
                                            children: (!recap || !branches.length) ? <Empty description="Aucune prime à reporter pour ces critères" /> : (
                                                <div>
                                                    {/* PRIME TOTALE À REPORTER par branche */}
                                                    <Card size="small" title="PRIME TOTALE À REPORTER" bordered={false}
                                                        style={{ borderRadius: 12, marginBottom: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                                                        <Row gutter={[12, 12]}>
                                                            {branches.map((br) => (
                                                                <Col xs={12} md={6} key={br}>
                                                                    <div style={{ border: "1px solid #eee", borderRadius: 10, padding: "10px 14px" }}>
                                                                        <div style={{ color: "#6b7280", fontWeight: 600 }}>{br}</div>
                                                                        <div style={{ fontSize: 18, fontWeight: 700, color: PRIMARY }}>
                                                                            {displayNumbers(recap.totauxParBranche?.[br] || 0)}
                                                                        </div>
                                                                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                                                                            nette {displayNumbers(recap.totauxNetteParBranche?.[br] || 0)}
                                                                            {" · "}acc. {displayNumbers(recap.totauxAccessoiresParBranche?.[br] || 0)}
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            ))}
                                                            <Col xs={12} md={6}>
                                                                <div style={{ border: "2px solid " + PRIMARY, borderRadius: 10, padding: "10px 14px", background: "#f6f6fb" }}>
                                                                    <div style={{ color: PRIMARY, fontWeight: 700 }}>TOTAL</div>
                                                                    <div style={{ fontSize: 18, fontWeight: 800, color: PRIMARY }}>
                                                                        {displayNumbers(recap.totalGeneral || 0)}
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Card>

                                                    <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                                                        <Table
                                                            size="small" bordered rowKey="key"
                                                            columns={repartitionColumns}
                                                            dataSource={repartitionRows}
                                                            pagination={false}
                                                            scroll={{ x: "max-content" }}
                                                            rowClassName={(r) => (r.isTotal ? "par-total-row" : "")}
                                                        />
                                                        <div style={{ marginTop: 12, color: "#6b7280", fontSize: 12 }}>
                                                            <div><strong>Méthode de calcul :</strong> Prorata temporis — assiette = prime nette + accessoires.</div>
                                                            <div><strong>NB :</strong> il revient à chaque société membre du Pool d'appliquer le taux approprié à la prime à reporter pour obtenir la PREC à provisionner.</div>
                                                            <div>Clés utilisées : clés de l'exercice {exercice}.</div>
                                                        </div>
                                                    </Card>
                                                </div>
                                            ),
                                        },
                                        {
                                            key: "detail", label: `Détail des contrats (${detail.length})`,
                                            children: (
                                                <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                                                    <Table
                                                        size="small" rowKey={(r, i) => `${r.numeroPolice}-${i}`}
                                                        columns={detailColumns}
                                                        dataSource={detail}
                                                        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} contrat(s)` }}
                                                        scroll={{ x: "max-content" }}
                                                        locale={{ emptyText: <Empty description="Aucun contrat" /> }}
                                                        summary={() => (
                                                            <Table.Summary fixed>
                                                                <Table.Summary.Row>
                                                                    <Table.Summary.Cell index={0} colSpan={6}><strong>TOTAL</strong></Table.Summary.Cell>
                                                                    <Table.Summary.Cell index={1} align="right"><strong>{displayNumbers(detailTotalPrime)}</strong></Table.Summary.Cell>
                                                                    <Table.Summary.Cell index={2} align="right"><strong>{displayNumbers(detailTotalAccessoires)}</strong></Table.Summary.Cell>
                                                                    <Table.Summary.Cell index={3} align="right"><strong>{displayNumbers(detailTotalAssiette)}</strong></Table.Summary.Cell>
                                                                    <Table.Summary.Cell index={4} align="right"><strong>{displayNumbers(detailTotal)}</strong></Table.Summary.Cell>
                                                                </Table.Summary.Row>
                                                            </Table.Summary>
                                                        )}
                                                    />
                                                </Card>
                                            ),
                                        },
                                    ]}
                                />
                            </Spin>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <style>{`.par-total-row > td { background: #f6f6fb !important; }`}</style>
        </div>
    );
};

export default PrimeAReporterEtat;
